import type { World } from '../sim/types';
import { civilFromEpochDay, epochDayOf } from '../sim/calendar';
import { atSea, dollars, fundsLevel, monthsInHand, planWait } from '../sim/operations';
import { managementOf } from './overview';
import { escapeHtml } from './format';

/** A month's first three letters, for the strip's short dates ("21 Jan"). */
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const shortDate = (epochDay: number): string => { const d = civilFromEpochDay(epochDay); return `${d.day} ${SHORT_MONTHS[d.month - 1]}`; };

/**
 * What the status strip shows (M294, the one-island roadmap's R4, the playtest's sixth point: "money, boat and active
 * projects always visible"): the funds and the months they hold at the costs that recur (`monthsInHand`, the accounts'
 * own figure); the boats, ready or at sea and back on a day; the work under way, counted. All read from the world each
 * time, none stored.
 */
export interface Status {
  funds: string;
  tone: 'good' | 'warn' | 'bad';
  months: string;
  boats: string;
  underWay: number;
}

/**
 * The work under way: each survey at sea or planned, each rule in its consultation or with the State, each liaison
 * training volunteers, each patrol on the water, each island's volunteers counting and a boat on order (M294's review:
 * the page it opens lists the projects).
 */
function underWayCount(world: World): number {
  const ops = world.operations;
  const patrols = managementOf(world)?.islands.filter((x) => x.patrol.level > 0).length ?? 0;
  const counting = ops.community.filter((c) => c !== null).length;
  // A boat counted while on order, not once in the water (review 33: the delivered boat stayed "under way" for good).
  const ordered = ops.projects.filter((p) => p.readyDay > epochDayOf(world)).length;
  return ops.jobs.length + ops.plan.length + patrols + counting + ordered;
}

export function statusOf(world: World): Status {
  const ops = world.operations;
  const months = monthsInHand(world);
  const n = Math.floor(months);
  const back = Array.from({ length: ops.boats }, (_, b) => atSea(ops).find((j) => j.boat === b)?.phaseEnds[0] ?? null);
  const out = back.filter((d): d is number => d !== null).sort((a, b) => a - b);
  // The next survey in the plan held with a boat in harbor, by the plan's own reason (`planWait`; M294's review: a crew
  // missing anywhere in the plan, and the money short, had read as ready).
  const wait = planWait(world);
  const held = wait === null ? null : wait.startsWith('it takes a captain') ? 'waiting for a crew' : wait.startsWith('its crew is not free') ? 'waiting for its crew' : 'held: not enough money';
  const boats = held !== null
    ? out.length === 0 ? `${ops.boats === 1 ? 'Boat' : 'Boats'} ${held}` : `One back ${shortDate(out[0] as number)}, one ${held}`
    : ops.boats === 1
      ? out.length === 0 ? 'Boat ready' : `Boat back ${shortDate(out[0] as number)}`
      : out.length === 0 ? 'Boats ready' : out.length < ops.boats ? `A boat ready, one back ${shortDate(out[0] as number)}` : `Boats back ${out.map(shortDate).join(' and ')}`;
  return {
    funds: dollars(ops.funds),
    tone: ({ good: 'good', fair: 'warn', poor: 'bad' } as const)[fundsLevel(world)],
    months: ops.funds < 0 ? 'in debt' : Number.isFinite(months) ? `${n} ${n === 1 ? 'month' : 'months'}` : '',
    boats,
    underWay: underWayCount(world),
  };
}

/** The strip: the funds (to the accounts), the boats, and the work under way (to the plan and the work's list). */
export function statusStripHtml(world: World): string {
  const s = statusOf(world);
  return `<button class="status-funds" data-act="status" data-kind="operations" title="Staff and accounts"><strong class="tone-${s.tone}">${escapeHtml(s.funds)}</strong>${s.months ? ` <span>${escapeHtml(s.months)}</span>` : ''}</button>
    <span class="status-boats">${escapeHtml(s.boats)}</span>
    <button class="status-work" data-act="status" data-kind="plan" title="The boat's plan and the work under way">${s.underWay} under way</button>`;
}
